import {Component, HostBinding, Input, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormGroup, Validators} from '@angular/forms';
import {Field} from '@app/interfaces';
import {Subscription} from 'rxjs';
import {FormDataService} from '@app/services/data/form-data.service';
import {FormInputPhotosComponent} from "@app/shared/briefd-form/field-types/form-input-photos/form-input-photos.component";

@Component({
    selector: 'app-form-input',
    templateUrl: './form-input.component.html',
    styleUrls: ['./form-input.component.scss'],
    standalone: false
})
export class FormInputComponent implements OnInit {

  @ViewChild(FormInputPhotosComponent) fieldInputPhotosComponent!: FormInputPhotosComponent;
  @Input() field: Field<any>;
  @Input() hideLabel = false;
  /** Flag tab this field is being edited under, and the dealer's own market —
   *  scopes the portal-usage indicator to that market (BRIEFD-7359). Null for
   *  fields that are not inside a per-country tab. */
  @Input() portalCountry: string | null = null;
  @Input() portalOwnCountry: string | null = null;
  @Input() showBooleanDescription = false;

  @Input() formGroup: FormGroup;

  private subscription: Subscription = new Subscription();

  @HostBinding('class.w-1/2') @Input() half = true;
  isHidden: any;

  constructor(
    private formDataService: FormDataService
  ) {
  }

  ngOnInit(): void {
    if (this.formGroup) {
      // @ts-ignore
      this.isHidden = this.formGroup.controls[this.field.fieldUuid].hidden;
      this.formGroup.controls[this.field.fieldUuid].valueChanges.subscribe((x) => {
        // @ts-ignore
        this.isHidden = this.formGroup.controls[this.field.fieldUuid].hidden;
      });
    }

    this.subscription.add(this.formDataService.fieldValueUpdates().subscribe((updateValues) => {
      if (updateValues !== null && updateValues !== undefined && updateValues[this.field.config.typeId] !== undefined && this.field.type !== 'repeater') {
        this.formGroup.controls[this.field.fieldUuid].setValue(updateValues[this.field.config.typeId]);
      }
    }));
  }

  /**
   * Whether this field is marked as required. Required is signalled three
   * different ways in the briefd-form payload:
   *   1. `field.config.isRequired === true` (legacy convenience flag).
   *   2. `field.validators` array contains the string `'required'` (the
   *      common authoritative source — `FormHelperService` reads it to
   *      attach `Validators.required`).
   *   3. `FormHelperService` may also implicitly attach `Validators.required`
   *      programmatically (e.g. file/photo fields with a `min:` count). The
   *      backend never sets `'required'` in those validator strings, but the
   *      control DOES carry the validator — so check the live FormControl
   *      directly as a final fallback.
   * The live control wins when there is one: 1 and 2 are the load-time
   * state, and an onSelect `notRequiredFields` (e.g. age type "Novo" releasing
   * mileage and first registration) only removes the control's validator —
   * reading the static config first kept the field red after it stopped
   * being required.
   */
  isRequired(): boolean {
    const ctrl: AbstractControl | undefined = this.formGroup?.controls?.[this.field?.fieldUuid];
    if (ctrl) { return ctrl.hasValidator(Validators.required); }

    if (this.field?.config?.isRequired) { return true; }
    const validators = this.field?.validators;
    return Array.isArray(validators) && validators.includes('required');
  }

  /** Required field with no value yet — drives the full red border. */
  isEmpty(): boolean {
    const v = this.formGroup?.controls?.[this.field?.fieldUuid]?.value;
    return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
  }

  keys(controls: {}) {
    return Object.keys(controls);
  }
  removeFiles() {
    if (this.fieldInputPhotosComponent) {
      this.fieldInputPhotosComponent.removeFiles();
    } else {
      console.error('Cannot remove files, component is undefined.');
    }
  }

  isBackgroundRemovalField(): boolean {
    if (this.fieldInputPhotosComponent) {
      return this.fieldInputPhotosComponent.isBackgroundRemovalField();
    }
    return false;
  }

  hasSelectedFiles(): boolean {
    return this.fieldInputPhotosComponent?.hasSelectedFiles() ?? false;
  }

}
